import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiConfig } from '../config';
import { useTranslation } from '../hooks/useTranslation';

interface ConfirmationResult {
  success: boolean;
  message: string;
  email?: string;
  alreadyConfirmed?: boolean;
}

const NewsletterConfirm: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [result, setResult] = useState<ConfirmationResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const confirmSubscription = async () => {
      if (!token) {
        setResult({
          success: false,
          message: t('newsletter_confirm.token_not_found')
        });
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/newsletter/confirm/${token}`);
        const data = await response.json();

        if (response.ok) {
          // Устанавливаем флаг подписки в localStorage при успешном подтверждении
          localStorage.setItem('vst_shop_newsletter_subscribed', 'true');
          
          setResult({
            success: true,
            message: data.message || t('newsletter_confirm.email_confirmed'),
            email: data.subscriber?.email,
            alreadyConfirmed: data.code === 'ALREADY_CONFIRMED'
          });
        } else {
          setResult({
            success: false,
            message: data.error || t('newsletter_confirm.confirmation_error')
          });
        }
      } catch (error) {
        console.error('Confirmation error:', error);
        setResult({
          success: false,
          message: t('newsletter_confirm.network_error')
        });
      } finally {
        setIsLoading(false);
      }
    };

    confirmSubscription();
  }, [token]);

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoToProfile = () => {
    navigate('/profile');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 border-4 border-purple-300 border-solid rounded-full animate-spin border-t-transparent mx-auto mb-4"></div>
            <div className="absolute inset-0 w-20 h-20 border-4 border-pink-300 border-solid rounded-full animate-ping border-t-transparent mx-auto opacity-75"></div>
          </div>
          <h2 className="text-2xl font-semibold text-white mb-2">{t('newsletter_confirm.confirming_subscription')}</h2>
          <p className="text-gray-300">{t('newsletter_confirm.please_wait')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 flex items-center justify-center">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      <div className="relative z-10 max-w-md w-full">
        <div className="bg-gray-800/50 backdrop-blur-lg border border-gray-700/50 rounded-2xl p-8 shadow-2xl">
          <div className="text-center">
            {result?.success ? (
              <>
                {/* Success Icon */}
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div className="absolute inset-0 w-20 h-20 bg-green-500 rounded-full mx-auto animate-ping opacity-75"></div>
                </div>

                <h1 className="text-3xl font-bold text-white mb-4">
                  {result.alreadyConfirmed ? t('newsletter_confirm.already_confirmed') : t('newsletter_confirm.successfully_confirmed')}
                </h1>
                
                <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 mb-6">
                  <p className="text-green-300 font-medium">
                    {result.message}
                  </p>
                  {result.email && (
                    <p className="text-green-400 text-sm mt-2">
                      Email: <span className="font-semibold">{result.email}</span>
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                {/* Error Icon */}
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center mx-auto">
                    <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <div className="absolute inset-0 w-20 h-20 bg-red-500 rounded-full mx-auto animate-pulse opacity-75"></div>
                </div>

                <h1 className="text-3xl font-bold text-white mb-4">
                  {t('newsletter_confirm.confirmation_error_title')}
                </h1>
                
                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6">
                  <p className="text-red-300 font-medium">
                    {result?.message || t('newsletter_confirm.confirmation_failed')}
                  </p>
                </div>

                <div className="bg-gray-700/50 rounded-lg p-4 mb-6">
                  <h3 className="text-white font-semibold mb-2 flex items-center">
                    <span className="mr-2">💡</span>
                    {t('newsletter_confirm.possible_reasons')}
                  </h3>
                  <ul className="text-gray-300 text-sm space-y-2">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                      {t('newsletter_confirm.reason_expired_link')}
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                      {t('newsletter_confirm.reason_already_confirmed')}
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></span>
                      {t('newsletter_confirm.reason_damaged_token')}
                    </li>
                  </ul>
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleGoHome}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
              >
                {t('newsletter_confirm.go_home_button')}
              </button>
              
              {result?.success && (
                <button
                  onClick={handleGoToProfile}
                  className="w-full bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300"
                >
                  {t('newsletter_confirm.go_to_profile_button')}
                </button>
              )}

              {!result?.success && (
                <p className="text-gray-400 text-sm">
                  {t('newsletter_confirm.need_help')} <a href="mailto:support@vstshop.com" className="text-purple-400 hover:text-purple-300 underline">{t('newsletter_confirm.contact_us')}</a>
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-gray-500 text-sm">
            {t('footer.copyright')}
          </p>
        </div>
      </div>

             <style>{`
         @keyframes blob {
           0% { transform: translate(0px, 0px) scale(1); }
           33% { transform: translate(30px, -50px) scale(1.1); }
           66% { transform: translate(-20px, 20px) scale(0.9); }
           100% { transform: translate(0px, 0px) scale(1); }
         }
         .animate-blob {
           animation: blob 7s infinite;
         }
         .animation-delay-2000 {
           animation-delay: 2s;
         }
         .animation-delay-4000 {
           animation-delay: 4s;
         }
       `}</style>
    </div>
  );
};

export default NewsletterConfirm; 